/**
 * Regenerating a review off the request path.
 *
 * The agents write every review nightly, so these exist for the case the night
 * run has not covered a subject yet or its inputs have moved since. Both
 * *enqueue* — `success` means the job was accepted, not that a review is ready,
 * so a caller refetches rather than reading a result out of the response.
 */
import { gql } from '@apollo/client';

export const RUN_MF_PM_REVIEW = gql`
  mutation RunMfPmReview($securitySymbol: String!, $window: String! = "monthly") {
    runMfPmReview(securitySymbol: $securitySymbol, window: $window) {
      success
      message
    }
  }
`;

export const RUN_FUND_MANAGER_REVIEW = gql`
  mutation RunFundManagerReview($managerName: String!, $window: String! = "monthly") {
    runFundManagerReview(managerName: $managerName, window: $window) {
      success
      message
    }
  }
`;

export const RUN_CLIENT_REVIEW = gql`
  mutation RunClientReview($window: String! = "daily") {
    runClientReview(window: $window) {
      success
      message
    }
  }
`;

export const RUN_ADVISOR_BOOK_REVIEW = gql`
  mutation RunAdvisorBookReview($window: String! = "weekly") {
    runAdvisorBookReview(window: $window) {
      success
      message
    }
  }
`;

/**
 * A model portfolio's review, run on demand.
 *
 * Same contract as the fund's: it ENQUEUES. `success` says the job was
 * accepted, not that a review exists — the caller refetches rather than
 * reading anything out of the response.
 */
export const RUN_MODEL_REVIEW = gql`
  mutation RunModelReview($modelId: Int!, $window: String! = "monthly") {
    runModelReview(modelId: $modelId, window: $window) {
      success
      message
    }
  }
`;

/**
 * A client portfolio's review, written again on demand.
 *
 * ENQUEUES, like the others: `success: false` with a message means nothing was
 * queued; `success: true` means a `portfolio.review.run` event is waiting, to
 * be followed through getTriggeredEvents. Each run is a paid LLM job, so the
 * caller must not send a second while one is queued or running.
 *
 * Only the review. `runPortfolioAnalytics` also ends in one, but recomputes
 * the whole portfolio and queues four other jobs on the way.
 */
export const RUN_PORTFOLIO_REVIEW = gql`
  mutation RunPortfolioReview($portfolioSecuritySymbol: String!, $window: String! = "daily") {
    runPortfolioReview(portfolioSecuritySymbol: $portfolioSecuritySymbol, window: $window) {
      success
      message
    }
  }
`;

/**
 * A client portfolio's review report.
 *
 * Unlike the others this one RENDERS: it answers with the report's id, its
 * status and, when the render finished inside the request, the pdf. Still
 * treated as enqueued by the screen, because `status` may come back pending.
 */
export const RENDER_PORTFOLIO_REVIEW = gql`
  mutation RenderPortfolioReviewReport($portfolioSecuritySymbol: String!, $window: String, $focus: String) {
    renderPortfolioReviewReport(
      portfolioSecuritySymbol: $portfolioSecuritySymbol
      window: $window
      focus: $focus
    ) {
      success
      message
      reportId
      status
      pdfUrl
    }
  }
`;

/** A scheme's own review, for the schemes the nightly run has not covered. */
export const RUN_MUTUAL_FUND_REVIEW = gql`
  mutation RunMutualFundReview($securitySymbol: String!) {
    runMutualFundReview(securitySymbol: $securitySymbol) {
      success
      message
    }
  }
`;

/**
 * A scheme's four-P research, written on demand — the run that publishes its
 * research report.
 *
 * ENQUEUES, like the others: one `mf.research.run` event per symbol, and the
 * edition renders its own report when the run ends, so there is no report in
 * the response to read. `quarterly` writes the research from scratch;
 * `monthly` updates the latest quarterly edition and needs one to exist. Each
 * run is a paid LLM job, so a caller must not send a second while one is in
 * hand.
 */
export const RUN_MF_RESEARCH = gql`
  mutation RunMfResearch($securitySymbols: [String!]!, $window: String! = "quarterly") {
    runMfResearch(securitySymbols: $securitySymbols, window: $window) {
      success
      message
    }
  }
`;
